import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import './LandingPage.css';
import { openCookieSettings } from '../utils/consent';
import logo from './classlabs.png';
import uclLogo from './ucllogo.png';
import lsvLogo from './LSV-Master-logo-1.png';
import lldcLogo from './London_Legacy_Development_Corporation.svg.png';
import qeopLogo from './sr16_qeop_logo_v2.png';
import googleLogo from './google.png';
import cdiLogo from './cdi.png';
import fairfordLogo from './Fairford.png';
import gainsboroughLogo from './gainsborough-logo.png';
import williamFordLogo from './william_ford.png';
import unitySchoolsLogo from './unity schools.png';
import illuminateMindsLogo from './illuminate minds.png';

const TypewriterText = ({ text, startDelay = 0, speed = 45 }) => {
  const [displayed, setDisplayed] = React.useState('');
  const [started, setStarted] = React.useState(false);
  React.useEffect(() => {
    setDisplayed('');
    setStarted(false);
    const t = setTimeout(() => setStarted(true), startDelay);
    return () => clearTimeout(t);
  }, [text, startDelay]);
  React.useEffect(() => {
    if (!started || displayed.length >= text.length) return;
    const t = setTimeout(() => setDisplayed(text.slice(0, displayed.length + 1)), speed);
    return () => clearTimeout(t);
  }, [started, displayed, text, speed]);
  const done = displayed.length >= text.length;
  return (
    <span>
      {displayed}
      {!done && <span style={{ display: 'inline-block', width: '2px', height: '1em', background: 'var(--charcoal)', marginLeft: '2px', verticalAlign: 'text-bottom', animation: 'cl-cursor-blink 0.6s step-end infinite' }} />}
    </span>
  );
};

const HERO_PANELS = [
  { step: '01', label: 'Upload content or choose Oak Academy', icon: '📤', bg: '#fff' },
  { step: '02', label: 'Build student profiles',               icon: '👤', bg: '#fff' },
  { step: '03', label: 'Each student receives bespoke tutorial', icon: '✨', bg: '#fff' },
  { step: '04', label: 'Automatic Analytics for Teachers, SENCos, SLT and Parents', icon: '📊', bg: '#fff' },
];

const HeroPanels = ({ current }) => {
  const p0KeyRef = React.useRef(0);
  const p1KeyRef = React.useRef(0);
  const p2KeyRef = React.useRef(0);
  const p3KeyRef = React.useRef(0);
  const prevCurrentRef = React.useRef(current);
  if (prevCurrentRef.current !== current) {
    prevCurrentRef.current = current;
    if (current === 0) p0KeyRef.current += 1;
    if (current === 1) p1KeyRef.current += 1;
    if (current === 2) p2KeyRef.current += 1;
    if (current === 3) p3KeyRef.current += 1;
  }
  const p0Key = p0KeyRef.current;
  const p1Key = p1KeyRef.current;
  const p2Key = p2KeyRef.current;
  const p3Key = p3KeyRef.current;

  const [p0Phase, setP0Phase] = React.useState('typing');
  React.useEffect(() => {
    setP0Phase('typing');
    const t1 = setTimeout(() => setP0Phase('contracting'), 2900);
    const t2 = setTimeout(() => setP0Phase('building'), 3750);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [p0Key]);

  const [p1Phase, setP1Phase] = React.useState('building');
  React.useEffect(() => {
    if (p1Key === 0) return;
    setP1Phase('typing');
    const t1 = setTimeout(() => setP1Phase('contracting'), 2250);
    const t2 = setTimeout(() => setP1Phase('building'), 3100);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [p1Key]);

  const [p2Phase, setP2Phase] = React.useState('building');
  React.useEffect(() => {
    if (p2Key === 0) return;
    setP2Phase('typing');
    const t1 = setTimeout(() => setP2Phase('contracting'), 3000);
    const t2 = setTimeout(() => setP2Phase('building'), 3850);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [p2Key]);

  const [p3Phase, setP3Phase] = React.useState('building');
  React.useEffect(() => {
    if (p3Key === 0) return;
    setP3Phase('typing');
    const t1 = setTimeout(() => setP3Phase('contracting'), 3000);
    const t2 = setTimeout(() => setP3Phase('building'), 3850);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [p3Key]);

  // FLIP refs — measure real header positions to land focal elements precisely (no opacity fade)
  const p0FocalBadgeRef = React.useRef(null);
  const p0FocalTitleRef = React.useRef(null);
  const p0BadgeRef = React.useRef(null);
  const p0TitleRef = React.useRef(null);
  const [p0BadgeET, setP0BadgeET] = React.useState(null);
  const [p0TitleET, setP0TitleET] = React.useState(null);
  React.useEffect(() => {
    if (p0Phase !== 'contracting') { setP0BadgeET(null); setP0TitleET(null); return; }
    const id = requestAnimationFrame(() => {
      if (p0FocalBadgeRef.current && p0BadgeRef.current) {
        const f = p0FocalBadgeRef.current.getBoundingClientRect();
        const r = p0BadgeRef.current.getBoundingClientRect();
        setP0BadgeET({ tx: (r.left+r.width/2)-(f.left+f.width/2), ty: (r.top+r.height/2)-(f.top+f.height/2), s: r.height/f.height });
      }
      if (p0FocalTitleRef.current && p0TitleRef.current) {
        const f = p0FocalTitleRef.current.getBoundingClientRect();
        const r = p0TitleRef.current.getBoundingClientRect();
        setP0TitleET({ tx: (r.left+r.width/2)-(f.left+f.width/2), ty: (r.top+r.height/2)-(f.top+f.height/2), s: r.height/f.height });
      }
    });
    return () => cancelAnimationFrame(id);
  }, [p0Phase]);

  const p1FocalBadgeRef = React.useRef(null);
  const p1FocalTitleRef = React.useRef(null);
  const p1BadgeRef = React.useRef(null);
  const p1TitleRef = React.useRef(null);
  const [p1BadgeET, setP1BadgeET] = React.useState(null);
  const [p1TitleET, setP1TitleET] = React.useState(null);
  React.useEffect(() => {
    if (p1Phase !== 'contracting') { setP1BadgeET(null); setP1TitleET(null); return; }
    const id = requestAnimationFrame(() => {
      if (p1FocalBadgeRef.current && p1BadgeRef.current) {
        const f = p1FocalBadgeRef.current.getBoundingClientRect();
        const r = p1BadgeRef.current.getBoundingClientRect();
        setP1BadgeET({ tx: (r.left+r.width/2)-(f.left+f.width/2), ty: (r.top+r.height/2)-(f.top+f.height/2), s: r.height/f.height });
      }
      if (p1FocalTitleRef.current && p1TitleRef.current) {
        const f = p1FocalTitleRef.current.getBoundingClientRect();
        const r = p1TitleRef.current.getBoundingClientRect();
        setP1TitleET({ tx: (r.left+r.width/2)-(f.left+f.width/2), ty: (r.top+r.height/2)-(f.top+f.height/2), s: r.height/f.height });
      }
    });
    return () => cancelAnimationFrame(id);
  }, [p1Phase]);

  const p2FocalBadgeRef = React.useRef(null);
  const p2FocalTitleRef = React.useRef(null);
  const p2BadgeRef = React.useRef(null);
  const p2TitleRef = React.useRef(null);
  const [p2BadgeET, setP2BadgeET] = React.useState(null);
  const [p2TitleET, setP2TitleET] = React.useState(null);
  React.useEffect(() => {
    if (p2Phase !== 'contracting') { setP2BadgeET(null); setP2TitleET(null); return; }
    const id = requestAnimationFrame(() => {
      if (p2FocalBadgeRef.current && p2BadgeRef.current) {
        const f = p2FocalBadgeRef.current.getBoundingClientRect();
        const r = p2BadgeRef.current.getBoundingClientRect();
        setP2BadgeET({ tx: (r.left+r.width/2)-(f.left+f.width/2), ty: (r.top+r.height/2)-(f.top+f.height/2), s: r.height/f.height });
      }
      if (p2FocalTitleRef.current && p2TitleRef.current) {
        const f = p2FocalTitleRef.current.getBoundingClientRect();
        const r = p2TitleRef.current.getBoundingClientRect();
        setP2TitleET({ tx: (r.left+r.width/2)-(f.left+f.width/2), ty: (r.top+r.height/2)-(f.top+f.height/2), s: r.height/f.height });
      }
    });
    return () => cancelAnimationFrame(id);
  }, [p2Phase]);

  const p3FocalBadgeRef = React.useRef(null);
  const p3FocalTitleRef = React.useRef(null);
  const p3BadgeRef = React.useRef(null);
  const p3TitleRef = React.useRef(null);
  const [p3BadgeET, setP3BadgeET] = React.useState(null);
  const [p3TitleET, setP3TitleET] = React.useState(null);
  React.useEffect(() => {
    if (p3Phase !== 'contracting') { setP3BadgeET(null); setP3TitleET(null); return; }
    const id = requestAnimationFrame(() => {
      if (p3FocalBadgeRef.current && p3BadgeRef.current) {
        const f = p3FocalBadgeRef.current.getBoundingClientRect();
        const r = p3BadgeRef.current.getBoundingClientRect();
        setP3BadgeET({ tx: (r.left+r.width/2)-(f.left+f.width/2), ty: (r.top+r.height/2)-(f.top+f.height/2), s: r.height/f.height });
      }
      if (p3FocalTitleRef.current && p3TitleRef.current) {
        const f = p3FocalTitleRef.current.getBoundingClientRect();
        const r = p3TitleRef.current.getBoundingClientRect();
        setP3TitleET({ tx: (r.left+r.width/2)-(f.left+f.width/2), ty: (r.top+r.height/2)-(f.top+f.height/2), s: r.height/f.height });
      }
    });
    return () => cancelAnimationFrame(id);
  }, [p3Phase]);

  return (
    <div style={{ position: 'absolute', inset: 0, overflow: 'hidden' }}>
      <div style={{
        display: 'flex',
        width: `${HERO_PANELS.length * 100}%`,
        height: '100%',
        transform: `translateX(-${current * (100 / HERO_PANELS.length)}%)`,
        transition: 'transform 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
      }}>
        {HERO_PANELS.map((panel, i) => (
          <div key={i} style={{
            width: `${100 / HERO_PANELS.length}%`,
            height: '100%',
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            alignItems: i === 0 ? 'stretch' : 'center',
            justifyContent: 'flex-start',
            padding: '20px 20px',
            gap: i === 1 ? '10px' : '12px',
            background: panel.bg,
          }}>
            {i === 1 ? (
              <React.Fragment key={p1Key}>
                {/* Phase 1+2: Large centred focal title */}
                {p1Phase !== 'building' && (
                  <div style={{
                    position: 'absolute', top: '50%', left: '50%', width: 'calc(100% - 40px)',
                    zIndex: 2, textAlign: 'center', pointerEvents: 'none',
                    transform: 'translate(-50%, -50%)',
                  }}>
                    <div ref={p1FocalBadgeRef} style={{
                      display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '14px', marginBottom: '14px',
                      animation: p1Phase === 'contracting' ? 'none' : 'cl-build-in 0.35s ease both',
                      animationDelay: '0s',
                      transition: p1Phase === 'contracting' ? 'transform 0.6s cubic-bezier(0.4,0,0.2,1)' : 'none',
                      transform: p1BadgeET ? `translate(${p1BadgeET.tx}px,${p1BadgeET.ty}px) scale(${p1BadgeET.s})` : 'none',
                    }}>
                      <span style={{ background: 'var(--teal)', color: '#fff', fontFamily: 'Nunito, sans-serif', fontWeight: 900, fontSize: '1.4rem', letterSpacing: '0.14em', textTransform: 'uppercase', padding: '8px 28px', borderRadius: '999px' }}>Step 02</span>
                      <span style={{ fontSize: '2.2rem' }}>{panel.icon}</span>
                    </div>
                    <div ref={p1FocalTitleRef} style={{
                      transition: p1Phase === 'contracting' ? 'transform 0.55s cubic-bezier(0.4,0,0.2,1) 0.08s' : 'none',
                      transform: p1TitleET ? `translate(${p1TitleET.tx}px,${p1TitleET.ty}px) scale(${p1TitleET.s})` : 'none',
                    }}>
                      <h3 style={{ fontFamily: 'Nunito, sans-serif', fontWeight: 900, fontSize: '2.2rem', color: 'var(--charcoal)', margin: 0, lineHeight: 1.25, whiteSpace: 'nowrap' }}>
                        <TypewriterText text={panel.label} startDelay={400} speed={38} />
                      </h3>
                    </div>
                  </div>
                )}
                {/* Real header — appears instantly when building starts */}
                <div style={{
                  display: 'flex', flexDirection: 'column', alignItems: 'flex-start', textAlign: 'left',
                  marginTop: '28px', width: '100%',
                  opacity: p1Phase === 'building' ? 1 : 0,
                }}>
                  <div ref={p1BadgeRef} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
                    <span style={{ background: 'var(--teal)', color: '#fff', fontFamily: 'Nunito, sans-serif', fontWeight: 900, fontSize: '0.6rem', letterSpacing: '0.1em', textTransform: 'uppercase', padding: '3px 10px', borderRadius: '999px' }}>Step 02</span>
                    <span style={{ fontSize: '1.1rem' }}>{panel.icon}</span>
                  </div>
                  <h3 ref={p1TitleRef} style={{ fontFamily: 'Nunito, sans-serif', fontWeight: 900, fontSize: '1.1rem', color: 'var(--charcoal)', margin: '0 0 6px 0', lineHeight: 1.2 }}>{panel.label}</h3>
                  <div style={{ width: '40px', height: '3px', background: 'var(--teal)', borderRadius: '2px' }} />
                </div>
                {/* Phase 3: Form card — only mount when building so animations play fresh */}
                {p1Phase === 'building' && <div style={{ width: '60%', background: '#fff', border: '2px solid var(--charcoal)', borderRadius: '14px', padding: '13px 15px', boxShadow: 'var(--sh-panel)', display: 'flex', flexDirection: 'column', gap: '9px', overflow: 'hidden', position: 'relative', animation: 'cl-build-in 0.35s ease both', animationDelay: '0.1s' }}>
                  <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '3px', background: 'var(--teal)' }} />
                  <div style={{ display: 'flex', gap: '8px', animation: 'cl-build-in 0.3s ease both', animationDelay: '0.25s' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: '0.58rem', fontWeight: 700, color: 'var(--teal)', marginBottom: '3px', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Name</div>
                      <div style={{ background: 'var(--field-bg)', border: '1.5px solid var(--field-border)', borderRadius: '7px', height: '26px', padding: '0 9px', display: 'flex', alignItems: 'center' }}>
                        <span style={{ fontSize: '0.7rem', color: 'var(--muted)' }}>Alex Johnson</span>
                      </div>
                    </div>
                    <div style={{ flex: '0 0 48px' }}>
                      <div style={{ fontSize: '0.58rem', fontWeight: 700, color: 'var(--teal)', marginBottom: '3px', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Age</div>
                      <div style={{ background: 'var(--field-bg)', border: '1.5px solid var(--field-border)', borderRadius: '7px', height: '26px', padding: '0 9px', display: 'flex', alignItems: 'center' }}>
                        <span style={{ fontSize: '0.7rem', color: 'var(--charcoal)', fontWeight: 700 }}>12</span>
                      </div>
                    </div>
                    <div style={{ flex: '0 0 70px' }}>
                      <div style={{ fontSize: '0.58rem', fontWeight: 700, color: 'var(--teal)', marginBottom: '3px', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Reading Age</div>
                      <div style={{ background: 'var(--field-bg)', border: '1.5px solid var(--field-border)', borderRadius: '7px', height: '26px', padding: '0 9px', display: 'flex', alignItems: 'center' }}>
                        <span style={{ fontSize: '0.7rem', color: 'var(--charcoal)', fontWeight: 700 }}>9</span>
                      </div>
                    </div>
                  </div>
                  <div style={{ animation: 'cl-build-in 0.3s ease both', animationDelay: '0.4s' }}>
                    <div style={{ fontSize: '0.58rem', fontWeight: 700, color: 'var(--teal)', marginBottom: '5px', textTransform: 'uppercase', letterSpacing: '0.06em' }}>SEN Condition</div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      {['Dyslexia', 'ADHD', 'Working Memory Difficulties', 'Processing Difficulties', 'Developmental Language Disorder', 'Language/Communication Needs', 'Autism Spectrum Condition'].map((cond, ci) => (
                        <div key={ci} style={{ display: 'flex', alignItems: 'center', gap: '7px', animation: 'cl-build-in 0.25s ease both', animationDelay: `${0.55 + ci * 0.1}s` }}>
                          <div style={{ width: '14px', height: '14px', borderRadius: '3px', border: '1.5px solid var(--charcoal)', background: [3,4,6].includes(ci) ? 'var(--teal)' : '#fff', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            {[3,4,6].includes(ci) && <span style={{ color: '#fff', fontSize: '0.6rem', fontWeight: 900 }}>✓</span>}
                          </div>
                          <span style={{ fontSize: '0.68rem', color: 'var(--charcoal)', fontWeight: [3,4,6].includes(ci) ? 700 : 400 }}>{cond}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', animation: 'cl-build-in 0.3s ease both', animationDelay: '1.3s' }}>
                    <div>
                      <div style={{ fontSize: '0.58rem', fontWeight: 700, color: 'var(--teal)', marginBottom: '3px', textTransform: 'uppercase', letterSpacing: '0.06em' }}>EAL Translation</div>
                      <div style={{ background: 'var(--field-bg)', border: '1.5px solid var(--field-border)', borderRadius: '7px', height: '26px', padding: '0 9px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <span style={{ fontSize: '0.68rem', color: 'var(--charcoal)' }}>Primary Language: Arabic</span>
                        <span style={{ fontSize: '0.6rem', color: 'var(--muted)' }}>▾</span>
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
                      <div style={{ width: '14px', height: '14px', borderRadius: '3px', border: '1.5px solid var(--charcoal)', background: 'var(--teal)', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <span style={{ color: '#fff', fontSize: '0.6rem', fontWeight: 900 }}>✓</span>
                      </div>
                      <span style={{ fontSize: '0.68rem', color: 'var(--charcoal)', fontWeight: 700 }}>Audio Narration</span>
                    </div>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'flex-end', animation: 'cl-build-pop 0.4s ease both', animationDelay: '1.5s' }}>
                    <div style={{ background: 'var(--teal)', color: '#fff', fontFamily: 'Nunito, sans-serif', fontWeight: 800, fontSize: '0.78rem', padding: '5px 14px', borderRadius: '8px', borderBottom: '2px solid rgba(0,0,0,0.12)' }}>Add Student</div>
                  </div>
                </div>}
              </React.Fragment>
            ) : i === 0 ? (
              <React.Fragment key={p0Key}>
                {/* Phase 1+2: Large centred focal title */}
                {p0Phase !== 'building' && (
                  <div style={{
                    position: 'absolute', top: '50%', left: '50%', width: 'calc(100% - 40px)',
                    zIndex: 2, textAlign: 'center', pointerEvents: 'none',
                    transform: 'translate(-50%, -50%)',
                  }}>
                    <div ref={p0FocalBadgeRef} style={{
                      display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '14px', marginBottom: '14px',
                      animation: p0Phase === 'contracting' ? 'none' : 'cl-build-in 0.35s ease both',
                      animationDelay: '0s',
                      transition: p0Phase === 'contracting' ? 'transform 0.6s cubic-bezier(0.4,0,0.2,1)' : 'none',
                      transform: p0BadgeET ? `translate(${p0BadgeET.tx}px,${p0BadgeET.ty}px) scale(${p0BadgeET.s})` : 'none',
                    }}>
                      <span style={{ background: 'var(--teal)', color: '#fff', fontFamily: 'Nunito, sans-serif', fontWeight: 900, fontSize: '1.4rem', letterSpacing: '0.14em', textTransform: 'uppercase', padding: '8px 28px', borderRadius: '999px' }}>Step 01</span>
                      <span style={{ fontSize: '2.2rem' }}>{panel.icon}</span>
                    </div>
                    <div ref={p0FocalTitleRef} style={{
                      transition: p0Phase === 'contracting' ? 'transform 0.55s cubic-bezier(0.4,0,0.2,1) 0.08s' : 'none',
                      transform: p0TitleET ? `translate(${p0TitleET.tx}px,${p0TitleET.ty}px) scale(${p0TitleET.s})` : 'none',
                    }}>
                      <h3 style={{ fontFamily: 'Nunito, sans-serif', fontWeight: 900, fontSize: '1.6rem', color: 'var(--charcoal)', margin: 0, lineHeight: 1.25, whiteSpace: 'nowrap' }}>
                        <TypewriterText text={panel.label} startDelay={400} speed={38} />
                      </h3>
                    </div>
                  </div>
                )}
                {/* Real header — appears instantly when building starts */}
                <div style={{
                  display: 'flex', flexDirection: 'column', alignItems: 'flex-start', textAlign: 'left',
                  marginTop: '28px',
                  opacity: p0Phase === 'building' ? 1 : 0,
                }}>
                  <div ref={p0BadgeRef} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
                    <span style={{ background: 'var(--teal)', color: '#fff', fontFamily: 'Nunito, sans-serif', fontWeight: 900, fontSize: '0.6rem', letterSpacing: '0.1em', textTransform: 'uppercase', padding: '3px 10px', borderRadius: '999px' }}>Step 01</span>
                    <span style={{ fontSize: '1.1rem' }}>{panel.icon}</span>
                  </div>
                  <h3 ref={p0TitleRef} style={{ fontFamily: 'Nunito, sans-serif', fontWeight: 900, fontSize: '1.25rem', color: 'var(--charcoal)', margin: '0 0 6px 0', lineHeight: 1.2 }}>{panel.label}</h3>
                  <div style={{ width: '40px', height: '3px', background: 'var(--teal)', borderRadius: '2px' }} />
                </div>
                {/* Phase 3: Cards — only mount when building so animations play fresh */}
                {p0Phase === 'building' && <div style={{ display: 'flex', flexDirection: 'column', gap: '0', marginTop: '18px' }}>
                  <div style={{ background: '#fff', border: '2px solid var(--charcoal)', borderRadius: '14px', padding: '11px 13px', boxShadow: 'var(--sh-panel)', display: 'flex', flexDirection: 'column', gap: '8px', animation: 'cl-build-in 0.35s ease both', animationDelay: '0.1s', position: 'relative', overflow: 'hidden' }}>
                    <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '3px', background: 'var(--teal)' }} />
                    <div style={{ fontFamily: 'Nunito, sans-serif', fontWeight: 900, fontSize: '0.82rem', color: 'var(--charcoal)' }}>Create New Lesson Plan</div>
                    <div style={{ display: 'flex', gap: '7px', alignItems: 'flex-end' }}>
                      <div style={{ flex: 1, minWidth: 0, animation: 'cl-build-in 0.3s ease both', animationDelay: '0.25s' }}>
                        <div style={{ fontSize: '0.58rem', fontWeight: 700, color: 'var(--teal)', marginBottom: '3px', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Plan Name</div>
                        <div style={{ background: 'var(--field-bg)', border: '1.5px solid var(--field-border)', borderRadius: '7px', height: '26px', padding: '0 9px', display: 'flex', alignItems: 'center' }}>
                          <span style={{ fontSize: '0.68rem', color: 'var(--muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>e.g. Geography – Rivers</span>
                        </div>
                      </div>
                      <div style={{ flex: '0 0 48px', animation: 'cl-build-in 0.3s ease both', animationDelay: '0.4s' }}>
                        <div style={{ fontSize: '0.58rem', fontWeight: 700, color: 'var(--teal)', marginBottom: '3px', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Year</div>
                        <div style={{ background: 'var(--field-bg)', border: '1.5px solid var(--field-border)', borderRadius: '7px', height: '26px', padding: '0 7px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <span style={{ fontSize: '0.68rem', color: 'var(--charcoal)', fontWeight: 700 }}>Y7</span>
                          <span style={{ fontSize: '0.55rem', color: 'var(--muted)' }}>▾</span>
                        </div>
                      </div>
                      <div style={{ flex: '0 0 68px', animation: 'cl-build-in 0.3s ease both', animationDelay: '0.55s' }}>
                        <div style={{ fontSize: '0.58rem', fontWeight: 700, color: 'var(--teal)', marginBottom: '3px', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Subject</div>
                        <div style={{ background: 'var(--field-bg)', border: '1.5px solid var(--field-border)', borderRadius: '7px', height: '26px', padding: '0 7px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <span style={{ fontSize: '0.68rem', color: 'var(--charcoal)', fontWeight: 700 }}>Geography</span>
                          <span style={{ fontSize: '0.55rem', color: 'var(--muted)' }}>▾</span>
                        </div>
                      </div>
                      <div style={{ background: 'var(--coral)', color: '#fff', fontFamily: 'Nunito, sans-serif', fontWeight: 800, fontSize: '0.7rem', padding: '0 11px', height: '26px', borderRadius: '7px', borderBottom: '2px solid rgba(0,0,0,0.14)', display: 'flex', alignItems: 'center', whiteSpace: 'nowrap', animation: 'cl-build-pop 0.4s ease both', animationDelay: '0.7s' }}>+ Create</div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '7px 4px', animation: 'cl-build-in 0.25s ease both', animationDelay: '0.82s' }}>
                    <div style={{ flex: 1, height: '1px', background: 'rgba(45,64,66,0.15)' }} />
                    <span style={{ fontFamily: 'Nunito, sans-serif', fontWeight: 700, fontSize: '0.62rem', color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>or</span>
                    <div style={{ flex: 1, height: '1px', background: 'rgba(45,64,66,0.15)' }} />
                  </div>
                  <div style={{ background: 'rgba(117,185,190,0.08)', border: '2px solid var(--teal)', borderRadius: '14px', padding: '11px 13px', boxShadow: 'var(--sh-panel-teal)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', animation: 'cl-build-in 0.35s ease both', animationDelay: '0.95s' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '9px' }}>
                      <span style={{ fontSize: '1.3rem' }}>📚</span>
                      <div>
                        <div style={{ fontFamily: 'Nunito, sans-serif', fontWeight: 900, fontSize: '0.78rem', color: 'var(--charcoal)' }}>Content Library</div>
                        <div style={{ fontSize: '0.6rem', color: 'var(--muted)', marginTop: '2px' }}>Pre-built Oak Academy lessons</div>
                      </div>
                    </div>
                    <div style={{ background: 'var(--teal)', color: '#fff', fontFamily: 'Nunito, sans-serif', fontWeight: 800, fontSize: '0.66rem', padding: '5px 11px', borderRadius: '7px', borderBottom: '2px solid rgba(0,0,0,0.1)', whiteSpace: 'nowrap' }}>Browse →</div>
                  </div>
                </div>}
              </React.Fragment>
            ) : i === 2 ? (
              <React.Fragment key={p2Key}>
                {/* Phase 1+2: Large centred focal title */}
                {p2Phase !== 'building' && (
                  <div style={{
                    position: 'absolute', top: '50%', left: '50%', width: 'calc(100% - 40px)',
                    zIndex: 2, textAlign: 'center', pointerEvents: 'none',
                    transform: 'translate(-50%, -50%)',
                  }}>
                    <div ref={p2FocalBadgeRef} style={{
                      display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '14px', marginBottom: '14px',
                      animation: p2Phase === 'contracting' ? 'none' : 'cl-build-in 0.35s ease both',
                      animationDelay: '0s',
                      transition: p2Phase === 'contracting' ? 'transform 0.6s cubic-bezier(0.4,0,0.2,1)' : 'none',
                      transform: p2BadgeET ? `translate(${p2BadgeET.tx}px,${p2BadgeET.ty}px) scale(${p2BadgeET.s})` : 'none',
                    }}>
                      <span style={{ background: 'var(--teal)', color: '#fff', fontFamily: 'Nunito, sans-serif', fontWeight: 900, fontSize: '1.4rem', letterSpacing: '0.14em', textTransform: 'uppercase', padding: '8px 28px', borderRadius: '999px' }}>Step 03</span>
                      <span style={{ fontSize: '2.2rem' }}>{panel.icon}</span>
                    </div>
                    <div ref={p2FocalTitleRef} style={{
                      transition: p2Phase === 'contracting' ? 'transform 0.55s cubic-bezier(0.4,0,0.2,1) 0.08s' : 'none',
                      transform: p2TitleET ? `translate(${p2TitleET.tx}px,${p2TitleET.ty}px) scale(${p2TitleET.s})` : 'none',
                    }}>
                      <h3 style={{ fontFamily: 'Nunito, sans-serif', fontWeight: 900, fontSize: '1.8rem', color: 'var(--charcoal)', margin: 0, lineHeight: 1.25, whiteSpace: 'nowrap' }}>
                        <TypewriterText text={panel.label} startDelay={400} speed={42} />
                      </h3>
                    </div>
                  </div>
                )}
                {/* Real header — appears instantly when building starts */}
                <div style={{
                  display: 'flex', flexDirection: 'column', alignItems: 'flex-start', textAlign: 'left',
                  marginTop: '28px', width: '100%',
                  opacity: p2Phase === 'building' ? 1 : 0,
                }}>
                  <div ref={p2BadgeRef} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
                    <span style={{ background: 'var(--teal)', color: '#fff', fontFamily: 'Nunito, sans-serif', fontWeight: 900, fontSize: '0.6rem', letterSpacing: '0.1em', textTransform: 'uppercase', padding: '3px 10px', borderRadius: '999px' }}>Step 03</span>
                    <span style={{ fontSize: '1.1rem' }}>{panel.icon}</span>
                  </div>
                  <h3 ref={p2TitleRef} style={{ fontFamily: 'Nunito, sans-serif', fontWeight: 900, fontSize: '1.1rem', color: 'var(--charcoal)', margin: '0 0 6px 0', lineHeight: 1.2 }}>{panel.label}</h3>
                  <div style={{ width: '40px', height: '3px', background: 'var(--teal)', borderRadius: '2px' }} />
                </div>
                {/* Phase 3: Tutorial grid — only mount when building so animations play fresh */}
                {p2Phase === 'building' && <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', width: '100%' }}>
                  {/* Key Terms Matching */}
                  <div style={{ background: '#fff', border: '2px solid var(--charcoal)', borderRadius: '12px', padding: '8px 10px', overflow: 'hidden', position: 'relative', animation: 'cl-build-in 0.3s ease both', animationDelay: '0.1s' }}>
                    <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '3px', background: 'var(--teal)' }} />
                    <div style={{ fontSize: '0.55rem', fontWeight: 700, color: 'var(--teal)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '6px' }}>Key Terms</div>
                    {[['Osmosis', 'Water movement'], ['Diffusion', 'Particle spread'], ['Respiration', 'Energy release']].map(([term, def], ti) => (
                      <div key={ti} style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '3px' }}>
                        <div style={{ background: 'rgba(117,185,190,0.15)', border: '1px solid var(--teal)', borderRadius: '4px', padding: '2px 5px', fontSize: '0.5rem', color: 'var(--charcoal)', fontWeight: 700, whiteSpace: 'nowrap' }}>{term}</div>
                        <div style={{ flex: 1, borderTop: '1px dashed rgba(45,64,66,0.25)' }} />
                        <div style={{ background: 'var(--field-bg)', border: '1px solid var(--field-border)', borderRadius: '4px', padding: '2px 5px', fontSize: '0.5rem', color: 'var(--muted)', whiteSpace: 'nowrap', overflow: 'hidden', maxWidth: '62px', textOverflow: 'ellipsis' }}>{def}</div>
                      </div>
                    ))}
                  </div>
                  {/* MCQ */}
                  <div style={{ background: '#fff', border: '2px solid var(--charcoal)', borderRadius: '12px', padding: '8px 10px', overflow: 'hidden', position: 'relative', animation: 'cl-build-in 0.3s ease both', animationDelay: '0.25s' }}>
                    <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '3px', background: 'var(--coral)' }} />
                    <div style={{ fontSize: '0.55rem', fontWeight: 700, color: 'var(--coral)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '4px' }}>MCQ</div>
                    <div style={{ fontSize: '0.52rem', color: 'var(--charcoal)', fontWeight: 700, marginBottom: '5px', lineHeight: 1.3 }}>What is osmosis?</div>
                    {['Water movement', 'Gas exchange', 'Cell division', 'Photosynthesis'].map((opt, oi) => (
                      <div key={oi} style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '2px' }}>
                        <div style={{ width: '9px', height: '9px', borderRadius: '50%', border: '1.5px solid ' + (oi === 0 ? 'var(--teal)' : 'var(--field-border)'), background: oi === 0 ? 'var(--teal)' : '#fff', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          {oi === 0 && <span style={{ color: '#fff', fontSize: '0.38rem', fontWeight: 900, lineHeight: 1 }}>✓</span>}
                        </div>
                        <span style={{ fontSize: '0.5rem', color: oi === 0 ? 'var(--charcoal)' : 'var(--muted)', fontWeight: oi === 0 ? 700 : 400 }}>{opt}</span>
                      </div>
                    ))}
                  </div>
                  {/* Sequencing */}
                  <div style={{ background: '#fff', border: '2px solid var(--charcoal)', borderRadius: '12px', padding: '8px 10px', overflow: 'hidden', position: 'relative', animation: 'cl-build-in 0.3s ease both', animationDelay: '0.4s' }}>
                    <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '3px', background: 'var(--teal)' }} />
                    <div style={{ fontSize: '0.55rem', fontWeight: 700, color: 'var(--teal)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '6px' }}>Sequencing</div>
                    {['Water absorbed by roots', 'Moves up the stem', 'Reaches the leaves', 'Evaporates via stomata'].map((step, si) => (
                      <div key={si} style={{ display: 'flex', alignItems: 'flex-start', gap: '5px', marginBottom: si < 3 ? '3px' : 0 }}>
                        <div style={{ width: '13px', height: '13px', borderRadius: '50%', background: 'var(--teal)', color: '#fff', fontSize: '0.45rem', fontWeight: 900, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: '1px' }}>{si + 1}</div>
                        <span style={{ fontSize: '0.5rem', color: 'var(--charcoal)', lineHeight: 1.3 }}>{step}</span>
                      </div>
                    ))}
                  </div>
                  {/* Categorisation */}
                  <div style={{ background: '#fff', border: '2px solid var(--charcoal)', borderRadius: '12px', padding: '8px 10px', overflow: 'hidden', position: 'relative', animation: 'cl-build-in 0.3s ease both', animationDelay: '0.55s' }}>
                    <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '3px', background: 'var(--coral)' }} />
                    <div style={{ fontSize: '0.55rem', fontWeight: 700, color: 'var(--coral)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '6px' }}>Categorisation</div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '5px' }}>
                      {[['Plants', ['Roots', 'Leaves', 'Chlorophyll']], ['Animals', ['Lungs', 'Heart', 'Neurons']]].map(([cat, items]) => (
                        <div key={cat}>
                          <div style={{ fontSize: '0.5rem', fontWeight: 700, color: 'var(--charcoal)', background: 'var(--field-bg)', border: '1px solid var(--field-border)', borderRadius: '4px', padding: '2px 5px', marginBottom: '3px', textAlign: 'center' }}>{cat}</div>
                          {items.map(item => (
                            <div key={item} style={{ fontSize: '0.48rem', color: 'var(--muted)', background: 'rgba(117,185,190,0.1)', borderRadius: '3px', padding: '1px 4px', marginBottom: '2px', textAlign: 'center' }}>{item}</div>
                          ))}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>}
              </React.Fragment>
            ) : (
              <React.Fragment key={p3Key}>
                {/* Phase 1+2: Large centred focal title */}
                {p3Phase !== 'building' && (
                  <div style={{
                    position: 'absolute', top: '50%', left: '50%', width: 'calc(100% - 40px)',
                    zIndex: 2, textAlign: 'center', pointerEvents: 'none',
                    transform: 'translate(-50%, -50%)',
                  }}>
                    <div ref={p3FocalBadgeRef} style={{
                      display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '14px', marginBottom: '14px',
                      animation: p3Phase === 'contracting' ? 'none' : 'cl-build-in 0.35s ease both',
                      animationDelay: '0s',
                      transition: p3Phase === 'contracting' ? 'transform 0.6s cubic-bezier(0.4,0,0.2,1)' : 'none',
                      transform: p3BadgeET ? `translate(${p3BadgeET.tx}px,${p3BadgeET.ty}px) scale(${p3BadgeET.s})` : 'none',
                    }}>
                      <span style={{ background: 'var(--teal)', color: '#fff', fontFamily: 'Nunito, sans-serif', fontWeight: 900, fontSize: '1.4rem', letterSpacing: '0.14em', textTransform: 'uppercase', padding: '8px 28px', borderRadius: '999px' }}>Step 04</span>
                      <span style={{ fontSize: '2.2rem' }}>{panel.icon}</span>
                    </div>
                    <div ref={p3FocalTitleRef} style={{
                      transition: p3Phase === 'contracting' ? 'transform 0.55s cubic-bezier(0.4,0,0.2,1) 0.08s' : 'none',
                      transform: p3TitleET ? `translate(${p3TitleET.tx}px,${p3TitleET.ty}px) scale(${p3TitleET.s})` : 'none',
                    }}>
                      <h3 style={{ fontFamily: 'Nunito, sans-serif', fontWeight: 900, fontSize: '1.5rem', color: 'var(--charcoal)', margin: 0, lineHeight: 1.3 }}>
                        <TypewriterText text={panel.label} startDelay={400} speed={28} />
                      </h3>
                    </div>
                  </div>
                )}
                {/* Real header — appears instantly when building starts */}
                <div style={{
                  display: 'flex', flexDirection: 'column', alignItems: 'flex-start', textAlign: 'left',
                  marginTop: '28px', width: '100%',
                  opacity: p3Phase === 'building' ? 1 : 0,
                }}>
                  <div ref={p3BadgeRef} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
                    <span style={{ background: 'var(--teal)', color: '#fff', fontFamily: 'Nunito, sans-serif', fontWeight: 900, fontSize: '0.6rem', letterSpacing: '0.1em', textTransform: 'uppercase', padding: '3px 10px', borderRadius: '999px' }}>Step 04</span>
                    <span style={{ fontSize: '1.1rem' }}>{panel.icon}</span>
                  </div>
                  <h3 ref={p3TitleRef} style={{ fontFamily: 'Nunito, sans-serif', fontWeight: 900, fontSize: '1.0rem', color: 'var(--charcoal)', margin: '0 0 6px 0', lineHeight: 1.2 }}>{panel.label}</h3>
                  <div style={{ width: '40px', height: '3px', background: 'var(--teal)', borderRadius: '2px' }} />
                </div>
                {/* Phase 3: Performance report — only mount when building so animations play fresh */}
                {p3Phase === 'building' && <div style={{ width: '100%', background: '#fff', border: '2px solid var(--charcoal)', borderRadius: '14px', padding: '11px 13px', boxShadow: 'var(--sh-panel)', display: 'flex', flexDirection: 'column', gap: '8px', overflow: 'hidden', position: 'relative', animation: 'cl-build-in 0.35s ease both', animationDelay: '0.1s' }}>
                  <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '3px', background: 'var(--teal)' }} />
                  {/* Stat chips row */}
                  <div style={{ display: 'flex', gap: '6px', animation: 'cl-build-in 0.3s ease both', animationDelay: '0.25s' }}>
                    {[['Class Avg', '74%', 'var(--teal)'], ['At Risk', '3', 'var(--coral)'], ['Top Performers', '5', 'var(--teal)']].map(([label, val, col]) => (
                      <div key={label} style={{ flex: 1, background: 'var(--field-bg)', border: `1.5px solid ${col}`, borderRadius: '8px', padding: '5px 6px', textAlign: 'center' }}>
                        <div style={{ fontSize: '0.82rem', fontWeight: 900, color: col, fontFamily: 'Nunito, sans-serif' }}>{val}</div>
                        <div style={{ fontSize: '0.48rem', color: 'var(--muted)', fontWeight: 600, marginTop: '1px' }}>{label}</div>
                      </div>
                    ))}
                  </div>
                  {/* Bar chart */}
                  <div style={{ animation: 'cl-build-in 0.3s ease both', animationDelay: '0.4s' }}>
                    <div style={{ fontSize: '0.55rem', fontWeight: 700, color: 'var(--teal)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '5px' }}>Score Distribution</div>
                    {[['0–25%', 1, 'var(--coral)'], ['26–50%', 3, 'var(--coral)'], ['51–75%', 8, 'var(--teal)'], ['76–100%', 5, 'var(--teal)']].map(([band, count, col]) => (
                      <div key={band} style={{ display: 'flex', alignItems: 'center', gap: '5px', marginBottom: '3px' }}>
                        <div style={{ width: '32px', fontSize: '0.45rem', color: 'var(--muted)', textAlign: 'right', flexShrink: 0 }}>{band}</div>
                        <div style={{ flex: 1, height: '8px', background: '#F0F2F5', borderRadius: '4px', overflow: 'hidden' }}>
                          <div style={{ width: `${(count / 8) * 100}%`, height: '100%', background: col, borderRadius: '4px' }} />
                        </div>
                        <div style={{ width: '10px', fontSize: '0.45rem', color: 'var(--muted)' }}>{count}</div>
                      </div>
                    ))}
                  </div>
                  {/* Student rows */}
                  <div style={{ animation: 'cl-build-in 0.3s ease both', animationDelay: '0.55s' }}>
                    <div style={{ fontSize: '0.55rem', fontWeight: 700, color: 'var(--teal)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '4px' }}>Students</div>
                    {[['Alex J.', 82, ['DLD']], ['Sam R.', 61, ['Dyslexia', 'EAL']], ['Jordan K.', 38, ['ADHD', 'PD']], ['Maya L.', 91, []]].map(([name, score, tags], si) => (
                      <div key={si} style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '3px', animation: 'cl-build-in 0.2s ease both', animationDelay: `${0.65 + si * 0.1}s` }}>
                        <div style={{ width: '20px', height: '20px', borderRadius: '50%', background: score >= 75 ? 'rgba(117,185,190,0.2)' : score >= 50 ? 'rgba(242,132,130,0.15)' : 'rgba(242,132,130,0.3)', border: `1.5px solid ${score >= 75 ? 'var(--teal)' : 'var(--coral)'}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          <span style={{ fontSize: '0.48rem', fontWeight: 700, color: score >= 75 ? 'var(--teal)' : 'var(--coral)' }}>{name.split(' ').map(n => n[0]).join('')}</span>
                        </div>
                        <span style={{ fontSize: '0.58rem', color: 'var(--charcoal)', fontWeight: 600, flex: 1 }}>{name}</span>
                        <div style={{ display: 'flex', gap: '3px' }}>
                          {tags.map(t => <span key={t} style={{ fontSize: '0.42rem', background: 'rgba(117,185,190,0.15)', color: 'var(--teal)', border: '1px solid var(--teal)', borderRadius: '3px', padding: '1px 4px', fontWeight: 700 }}>{t}</span>)}
                        </div>
                        <span style={{ fontSize: '0.6rem', fontWeight: 900, color: score >= 75 ? 'var(--teal)' : score >= 50 ? 'var(--charcoal)' : 'var(--coral)', fontFamily: 'Nunito, sans-serif' }}>{score}%</span>
                      </div>
                    ))}
                  </div>
                </div>}
              </React.Fragment>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

const CheckCircle = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, stroke: 'var(--teal)' }}>
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
    <polyline points="22 4 12 14.01 9 11.01" />
  </svg>
);

const IMPACTS = [
  '50% reduction in teacher planning time',
  'Sustained focus & engagement (ADHD)',
  'Improved autonomy & confidence (Autism)',
  'Stronger communication (SLCN)',
];

// Five-star impact-study rating with the impacts rotating underneath.
const ImpactRotator = () => {
  const [i, setI] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setI((x) => (x + 1) % IMPACTS.length), 2800);
    return () => clearInterval(t);
  }, []);
  const jump = (e) => {
    e.preventDefault();
    const el = document.getElementById('testimonials');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };
  return (
    <a className="cl-impact-rotator" href="#testimonials" onClick={jump}>
      <div className="cl-impact-rotator-head">
        <img src={williamFordLogo} alt="William Ford" className="cl-impact-badge" />
        <div className="cl-impact-rating">
          <span className="cl-impact-stars">★★★★★</span>
          <span className="cl-impact-source">William Ford C of E Impact Study <span className="cl-impact-arrow" aria-hidden="true">→</span></span>
        </div>
      </div>
      <div className="cl-impact-rotator-line">
        <CheckCircle />
        <span key={i} className="cl-impact-rotator-text">{IMPACTS[i]}</span>
      </div>
    </a>
  );
};

const STATS = [
  { target: 50, prefix: '', suffix: '%', comma: false, desc: 'of teacher planning and content adaptation time reduced' },
  { target: 400, prefix: '', suffix: '', comma: false, desc: 'hours of tutorials delivered' },
];

// SEND-condition benefits (mirrors the impacts rotating in the hero).
const SEND_BENEFITS = [
  { cond: 'ADHD', benefit: 'Sustained focus & engagement' },
  { cond: 'Autism', benefit: 'Improved autonomy & confidence' },
  { cond: 'SLCN', benefit: 'Stronger communication' },
];

// White stats banner whose numbers count up when it scrolls into view.
const StatsBanner = () => {
  const ref = React.useRef(null);
  const [vals, setVals] = useState(() => STATS.map(() => 0));

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setVals(STATS.map(s => s.target));
      return;
    }
    let done = false;
    const io = new IntersectionObserver((entries) => {
      if (done || !entries.some(e => e.isIntersecting)) return;
      done = true;
      io.disconnect();
      const duration = 1600;
      const t0 = performance.now();
      const tick = (now) => {
        const p = Math.min((now - t0) / duration, 1);
        const eased = 1 - Math.pow(1 - p, 3);
        setVals(STATS.map(s => Math.round(s.target * eased)));
        if (p < 1) requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    }, { threshold: 0.4 });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  const fmt = (n, s) => `${s.prefix}${s.comma ? n.toLocaleString('en-GB') : n}${s.suffix}`;

  return (
    <section className="cl-stats-banner" ref={ref}>
      <div className="cl-wrap">
        <div className="cl-stats-grid">
          {STATS.map((s, i) => (
            <div className="cl-stat-item" key={i}>
              <span className="cl-stat-num">{fmt(vals[i], s)}</span>
              <span className="cl-stat-desc">{s.desc}</span>
            </div>
          ))}
          <div className="cl-stat-item cl-stat-send">
            {SEND_BENEFITS.map((b) => (
              <span className="cl-stat-send-item" key={b.cond}>
                <span className="cl-stat-send-cond">{b.cond}</span>
                <span className="cl-stat-send-benefit">{b.benefit}</span>
              </span>
            ))}
          </div>
        </div>
        <p className="cl-stats-label">William Ford C of E Junior School Impact Study 2026</p>
      </div>
    </section>
  );
};

// "Create a lesson plan" card — the built state of the hero's step-1 panel
// (Create New Lesson Plan + Content Library). Build-in animation replays each
// time it scrolls into view. HeroPanels itself is left untouched.
const LessonPlanDemo = () => {
  const ref = React.useRef(null);
  const [playKey, setPlayKey] = useState(0);
  const shownRef = React.useRef(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting && !shownRef.current) { shownRef.current = true; setPlayKey((k) => k + 1); }
        else if (!e.isIntersecting) { shownRef.current = false; }
      });
    }, { threshold: 0.4 });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return (
    <div className="cl-offer-demo" ref={ref}>
      <div key={playKey} style={{ display: 'flex', flexDirection: 'column', gap: '0', width: '100%' }}>
        <div style={{ background: '#fff', border: '2px solid var(--charcoal)', borderRadius: '14px', padding: '11px 13px', boxShadow: 'var(--sh-panel)', display: 'flex', flexDirection: 'column', gap: '8px', position: 'relative', overflow: 'hidden', animation: 'cl-build-in 0.35s ease both', animationDelay: '0.1s' }}>
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '3px', background: 'var(--teal)' }} />
          <div style={{ fontFamily: 'Nunito, sans-serif', fontWeight: 900, fontSize: '0.82rem', color: 'var(--charcoal)' }}>Create New Lesson Plan</div>
          <div style={{ display: 'flex', gap: '7px', alignItems: 'flex-end' }}>
            <div style={{ flex: 1, minWidth: 0, animation: 'cl-build-in 0.3s ease both', animationDelay: '0.25s' }}>
              <div style={{ fontSize: '0.58rem', fontWeight: 700, color: 'var(--teal)', marginBottom: '3px', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Plan Name</div>
              <div style={{ background: 'var(--field-bg)', border: '1.5px solid var(--field-border)', borderRadius: '7px', height: '26px', padding: '0 9px', display: 'flex', alignItems: 'center' }}>
                <span style={{ fontSize: '0.68rem', color: 'var(--muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>e.g. Geography – Rivers</span>
              </div>
            </div>
            <div style={{ flex: '0 0 48px', animation: 'cl-build-in 0.3s ease both', animationDelay: '0.4s' }}>
              <div style={{ fontSize: '0.58rem', fontWeight: 700, color: 'var(--teal)', marginBottom: '3px', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Year</div>
              <div style={{ background: 'var(--field-bg)', border: '1.5px solid var(--field-border)', borderRadius: '7px', height: '26px', padding: '0 7px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ fontSize: '0.68rem', color: 'var(--charcoal)', fontWeight: 700 }}>Y7</span>
                <span style={{ fontSize: '0.55rem', color: 'var(--muted)' }}>▾</span>
              </div>
            </div>
            <div style={{ flex: '0 0 68px', animation: 'cl-build-in 0.3s ease both', animationDelay: '0.55s' }}>
              <div style={{ fontSize: '0.58rem', fontWeight: 700, color: 'var(--teal)', marginBottom: '3px', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Subject</div>
              <div style={{ background: 'var(--field-bg)', border: '1.5px solid var(--field-border)', borderRadius: '7px', height: '26px', padding: '0 7px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ fontSize: '0.68rem', color: 'var(--charcoal)', fontWeight: 700 }}>Geography</span>
                <span style={{ fontSize: '0.55rem', color: 'var(--muted)' }}>▾</span>
              </div>
            </div>
            <div style={{ background: 'var(--coral)', color: '#fff', fontFamily: 'Nunito, sans-serif', fontWeight: 800, fontSize: '0.7rem', padding: '0 11px', height: '26px', borderRadius: '7px', borderBottom: '2px solid rgba(0,0,0,0.14)', display: 'flex', alignItems: 'center', whiteSpace: 'nowrap', animation: 'cl-build-pop 0.4s ease both', animationDelay: '0.7s' }}>+ Create</div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '7px 4px', animation: 'cl-build-in 0.25s ease both', animationDelay: '0.82s' }}>
          <div style={{ flex: 1, height: '1px', background: 'rgba(45,64,66,0.15)' }} />
          <span style={{ fontFamily: 'Nunito, sans-serif', fontWeight: 700, fontSize: '0.62rem', color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>or</span>
          <div style={{ flex: 1, height: '1px', background: 'rgba(45,64,66,0.15)' }} />
        </div>
        <div style={{ background: 'rgba(117,185,190,0.08)', border: '2px solid var(--teal)', borderRadius: '14px', padding: '11px 13px', boxShadow: 'var(--sh-panel-teal)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', animation: 'cl-build-in 0.35s ease both', animationDelay: '0.95s' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '9px' }}>
            <span style={{ fontSize: '1.3rem' }}>📚</span>
            <div>
              <div style={{ fontFamily: 'Nunito, sans-serif', fontWeight: 900, fontSize: '0.78rem', color: 'var(--charcoal)' }}>Content Library</div>
              <div style={{ fontSize: '0.6rem', color: 'var(--muted)', marginTop: '2px' }}>Pre-built Oak Academy lessons</div>
            </div>
          </div>
          <div style={{ background: 'var(--teal)', color: '#fff', fontFamily: 'Nunito, sans-serif', fontWeight: 800, fontSize: '0.66rem', padding: '5px 11px', borderRadius: '7px', borderBottom: '2px solid rgba(0,0,0,0.1)', whiteSpace: 'nowrap' }}>Browse →</div>
        </div>
      </div>
    </div>
  );
};

const FEATURES = [
  { icon: '🎚️', label: 'Dynamic difficulty', key: 'difficulty' },
  { icon: '🔊', label: 'Read aloud', key: 'tts' },
  { icon: '📖', label: 'Reading-age matched', key: 'reading' },
  { icon: '🎨', label: 'Colour overlays', key: 'colour' },
  { icon: '🔤', label: 'Dyslexia-friendly font', key: 'font' },
  { icon: '🌍', label: 'Home-language support', key: 'translate' },
];

const renderFeatureDemo = (key) => {
  if (key === 'difficulty') {
    return (
      <div className="cl-feat-card">
        <div className="cl-feat-title">Adapts as the pupil works</div>
        <div className="cl-feat-diffrow">
          <span className="cl-feat-pill">Easier</span>
          <span className="cl-feat-pill on">On level</span>
          <span className="cl-feat-pill">Stretch</span>
        </div>
        <div className="cl-mock-line" style={{ width: '92%' }} />
        <div className="cl-mock-line short" />
      </div>
    );
  }
  if (key === 'tts') {
    return (
      <div className="cl-feat-card">
        <div className="cl-feat-title">Every tutorial read aloud</div>
        <div className="cl-feat-ttsrow">
          <span className="cl-feat-play">▶</span>
          <div className="cl-feat-wave">
            {[0, 1, 2, 3, 4, 5, 6].map((i) => <span key={i} style={{ animationDelay: `${i * 0.11}s` }} />)}
          </div>
        </div>
        <div className="cl-feat-read"><span className="cl-feat-word on">The river</span> flows down to the sea.</div>
      </div>
    );
  }
  if (key === 'reading') {
    return (
      <div className="cl-feat-card">
        <div className="cl-feat-title">Matched to reading age</div>
        <div className="cl-feat-chips"><span className="cl-mock-tag on">Reading age 9</span></div>
        <div className="cl-mock-line" style={{ width: '95%' }} />
        <div className="cl-mock-line" style={{ width: '72%' }} />
        <div className="cl-mock-line short" />
      </div>
    );
  }
  if (key === 'colour') {
    return (
      <div className="cl-feat-card">
        <div className="cl-feat-title">Reading colour overlays</div>
        <div className="cl-feat-swatches">
          <span className="on" style={{ background: '#FFF3C4' }} />
          <span style={{ background: '#D6ECFF' }} />
          <span style={{ background: '#FFE0EC' }} />
          <span style={{ background: '#DDF3E0' }} />
        </div>
        <div className="cl-feat-overlay">The river flows down to the sea, past the old stone bridge.</div>
      </div>
    );
  }
  if (key === 'font') {
    return (
      <div className="cl-feat-card">
        <div className="cl-feat-title">Dyslexia-friendly font</div>
        <div className="cl-feat-chips"><span className="cl-mock-tag on">OpenDyslexic</span></div>
        <div className="cl-feat-fontsample">The river flows down to the sea.</div>
      </div>
    );
  }
  return (
    <div className="cl-feat-card">
      <div className="cl-feat-title">Home-language support</div>
      <div className="cl-feat-chips"><span className="cl-mock-tag on">English → العربية</span></div>
      <div className="cl-feat-translate">
        <div>The river flows down to the sea.</div>
        <div dir="rtl">يتدفق النهر نزولاً إلى البحر.</div>
      </div>
    </div>
  );
};

// Cycling showcase of adaptive tutorial features, shown in the step-2 box.
const FeatureShowcase = () => {
  const [current, setCurrent] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setCurrent((i) => (i + 1) % FEATURES.length), 3400);
    return () => clearInterval(t);
  }, []);
  const f = FEATURES[current];
  return (
    <div className="cl-feat-stage">
      <div className="cl-feat-panel" key={current}>
        <div className="cl-feat-badge"><span>{f.icon}</span>{f.label}</div>
        {renderFeatureDemo(f.key)}
      </div>
      <div className="cl-feat-dots">
        {FEATURES.map((_, i) => <span key={i} className={`cl-feat-dot${i === current ? ' active' : ''}`} />)}
      </div>
    </div>
  );
};

const LandingPage = () => {
  const [heroCurrent, setHeroCurrent] = useState(0);
  const [menuOpen, setMenuOpen] = useState(false);
  const heroPrev = () => setHeroCurrent(i => (i - 1 + HERO_PANELS.length) % HERO_PANELS.length);
  const heroNext = () => setHeroCurrent(i => (i + 1) % HERO_PANELS.length);
  useEffect(() => {
    const t = setTimeout(() => setHeroCurrent(i => (i + 1) % HERO_PANELS.length), 6000);
    return () => clearTimeout(t);
  }, [heroCurrent]);

  const handleHowItWorksClick = (e) => {
    e.preventDefault();
    const section = document.getElementById('offers');
    if (section) {
      section.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };


  return (
    <div className="cl-page">
      <div className="cl-pattern-bg" />

      {/* Nav */}
      <nav className="cl-nav">
        <div className="cl-nav-inner">
          <div className="cl-nav-spacer">
            <button
              type="button"
              className="cl-nav-burger"
              aria-label="Menu"
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen((o) => !o)}
            >
              <span /><span /><span />
            </button>
          </div>
          <div className="cl-nav-logo-group">
            <Link to="/">
              <img src={logo} alt="ClassLabs" className="cl-logo-main" />
            </Link>
          </div>
          <div className="cl-nav-actions">
            <Link to="/login" className="cl-nav-signin">Log In</Link>
            <Link to="/register-interest" className="cl-nav-cta">Book Demo</Link>
          </div>
        </div>
      </nav>

      {/* Slide-in side nav */}
      <div
        className={`cl-drawer-backdrop ${menuOpen ? 'is-open' : ''}`}
        onClick={() => setMenuOpen(false)}
      />
      <aside className={`cl-drawer ${menuOpen ? 'is-open' : ''}`} aria-hidden={!menuOpen}>
        <button
          type="button"
          className="cl-drawer-close"
          aria-label="Close menu"
          onClick={() => setMenuOpen(false)}
        >
          ×
        </button>
        <nav className="cl-drawer-links">
          <Link to="/" onClick={() => setMenuOpen(false)}>Home</Link>
          <a href="#offers" onClick={() => setMenuOpen(false)}>How it works</a>
          <a href="#testimonials" onClick={() => setMenuOpen(false)}>What schools say</a>
          <Link to="/sensiblesencoreview" onClick={() => setMenuOpen(false)}>SENDCO review</Link>
        </nav>
        <div className="cl-drawer-actions">
          <Link to="/login" className="cl-drawer-signin" onClick={() => setMenuOpen(false)}>Log In</Link>
          <Link to="/register-interest" className="cl-drawer-cta" onClick={() => setMenuOpen(false)}>Book Demo</Link>
        </div>
      </aside>

      {/* Hero */}
      <div className="cl-wrap">
        <header className="cl-hero">
          <div className="cl-hero-grid">
            <div className="cl-hero-text">
              <div className="cl-hero-spacer" />
              <h1 className="cl-h1">
                Every child learns differently. <span style={{ color: 'var(--teal-deep)', textDecoration: 'underline', textDecorationColor: 'var(--coral)', textDecorationThickness: '6.75px', textUnderlineOffset: '4px' }}>Now every child can.</span>
              </h1>
              <p className="cl-hero-lede">
                The Digital Teaching Assistant Trusted by UK SEND Units. <strong style={{ color: 'var(--charcoal)', fontWeight: 700 }}>Backed by evidence. Built for inclusion.</strong>
              </p>
              <div className="cl-hero-ctas">
                <Link to="/register-interest" className="cl-btn-primary">Book Demo</Link>
                <button onClick={handleHowItWorksClick} className="cl-btn-outline">Learn More</button>
              </div>
              <ImpactRotator />
            </div>

            <div className="cl-hero-video-wrap">
              <div className="cl-hero-video-box">
                <HeroPanels current={heroCurrent} />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginTop: '24px' }}>
                <button onClick={heroPrev} style={{ background: 'var(--charcoal)', border: 'none', borderRadius: '50%', width: '22px', height: '22px', color: '#fff', fontSize: '0.75rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1 }}>‹</button>
                {HERO_PANELS.map((_, i) => (
                  <div key={i} style={{ width: i === heroCurrent ? '18px' : '6px', height: '6px', borderRadius: '999px', background: i === heroCurrent ? 'var(--charcoal)' : 'rgba(45,64,66,0.25)', transition: 'all 0.3s ease' }} />
                ))}
                <button onClick={heroNext} style={{ background: 'var(--charcoal)', border: 'none', borderRadius: '50%', width: '22px', height: '22px', color: '#fff', fontSize: '0.75rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1 }}>›</button>
              </div>
            </div>
          </div>
        </header>
      </div>

      {/* Trusted by schools */}
      <section className="cl-supported">
        <div className="cl-wrap">
          <p className="cl-supported-label">Trusted by schools</p>
          <div className="cl-supported-logos cl-supported-logos--home">
            <div className="cl-supported-logos-track">
              {[
                { src: fairfordLogo,        alt: 'Fairford' },
                { src: qeopLogo,            alt: 'Queen Elizabeth Olympic Park',  height: 77 },
                { src: gainsboroughLogo,    alt: 'Gainsborough',                  height: 92 },
                { src: williamFordLogo,     alt: 'William Ford' },
                { src: unitySchoolsLogo,    alt: 'Unity Schools',                 height: 62 },
                { src: illuminateMindsLogo, alt: 'Illuminate Minds' },
              ].concat([
                { src: fairfordLogo,        alt: 'Fairford-2' },
                { src: qeopLogo,            alt: 'QEOP-2',                        height: 77 },
                { src: gainsboroughLogo,    alt: 'Gainsborough-2',                height: 92 },
                { src: williamFordLogo,     alt: 'William Ford-2' },
                { src: unitySchoolsLogo,    alt: 'Unity Schools-2',               height: 62 },
                { src: illuminateMindsLogo, alt: 'Illuminate Minds-2' },
              ]).map(({ src, alt, height }) => (
                <img
                  key={alt}
                  src={src}
                  alt={alt}
                  className="cl-supported-img"
                  style={height ? { height } : {}}
                  onMouseEnter={e => { e.currentTarget.style.filter = 'grayscale(0%)'; e.currentTarget.style.opacity = '1'; }}
                  onMouseLeave={e => { e.currentTarget.style.filter = 'grayscale(100%)'; e.currentTarget.style.opacity = '0.6'; }}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* What ClassLabs offers schools */}
      <section className="cl-section cl-offers" id="offers">
        <div className="cl-wrap">
          <div className="cl-section-head cl-section-head--center">
            <div className="cl-eyebrow">How ClassLabs works</div>
            <h2 className="cl-h2">Dynamic difficulty adjustment. Bespoke student adaptations. <span className="cl-h2-accent">Inclusion by design.</span></h2>
          </div>
          <p className="cl-review-byline">Independently reviewed by <Link className="cl-review-byline-link" to="/sensiblesencoreview">Abigail Hawkins, SENDCO Solutions →</Link></p>
          <div className="cl-offer-rows">
            <div className="cl-offer-row">
              <div className="cl-offer-copy">
                <div className="cl-offer-head">
                  <span className="cl-offer-badge" style={{ background: 'var(--teal)' }}>1</span>
                  <h3>Create a lesson plan</h3>
                </div>
                <p><span className="cl-quote-name">Abigail Hawkins:</span> “The platform allows teachers to <span className="cl-emph">upload their own lesson materials</span> (PDFs, Word documents, PowerPoints) or draw on a <span className="cl-emph">library of over 4,400 Oak National Academy resources</span>.”</p>              </div>
              <div className="cl-offer-visual" style={{ '--tint': 'var(--teal)', background: '#fff', height: 240 }}>
                <LessonPlanDemo />
              </div>
            </div>

            <div className="cl-offer-row cl-offer-row--rev">
              <div className="cl-offer-copy">
                <div className="cl-offer-head">
                  <span className="cl-offer-badge" style={{ background: 'var(--coral)' }}>2</span>
                  <h3>Adapted tutorials and worksheets</h3>
                </div>
                <p><span className="cl-quote-name">Abigail Hawkins:</span> “The AI identifies learning objectives and Tier 3 vocabulary from the uploaded content, then generates <span className="cl-emph">individually tailored tutorials for each pupil</span> based on their reading age, SEN profile, and home language.”</p>              </div>
              <div className="cl-offer-visual" style={{ '--tint': 'var(--coral)', background: '#fff', height: 240 }}>
                <FeatureShowcase />
              </div>
            </div>

            <div className="cl-offer-row">
              <div className="cl-offer-copy">
                <div className="cl-offer-head">
                  <span className="cl-offer-badge" style={{ background: 'var(--teal)' }}>3</span>
                  <h3>Analysis and reporting</h3>
                </div>
                <p><span className="cl-quote-name">Abigail Hawkins:</span> “That evidence trail has value well beyond the individual lesson. <span className="cl-emph">It supports APDR cycles, annual reviews, ISPs, and conversations with external professionals</span>: educational psychologists, speech and language therapists, and others.”</p>              </div>
              <div className="cl-offer-visual" style={{ '--tint': 'var(--teal)' }}>
                <div className="cl-mock">
                  <div className="cl-mock-bar" />
                  <div className="cl-mock-head">
                    <div className="cl-mock-title">Class progress</div>
                    <span className="cl-mock-pill">EHCP ready</span>
                  </div>
                  <div className="cl-mock-prow"><span className="cl-mock-pname">Alex J.</span><div className="cl-mock-track"><div className="cl-mock-fill" style={{ width: '82%' }} /></div><span className="cl-mock-pct">82%</span></div>
                  <div className="cl-mock-prow"><span className="cl-mock-pname">Priya S.</span><div className="cl-mock-track"><div className="cl-mock-fill" style={{ width: '64%' }} /></div><span className="cl-mock-pct">64%</span></div>
                  <div className="cl-mock-prow"><span className="cl-mock-pname">Marcus B.</span><div className="cl-mock-track"><div className="cl-mock-fill" style={{ width: '91%' }} /></div><span className="cl-mock-pct">91%</span></div>
                  <div className="cl-mock-prow"><span className="cl-mock-pname">Sara K.</span><div className="cl-mock-track"><div className="cl-mock-fill" style={{ width: '73%' }} /></div><span className="cl-mock-pct">73%</span></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Explained by the experts */}
      <section className="cl-section cl-testi" id="experts">
        <div className="cl-wrap">
          <div className="cl-section-head cl-section-head--center">
            <div className="cl-eyebrow">Reviewed by the experts</div>
            <h2 className="cl-h2">“Keeps the children <span className="cl-h2-accent">engaged</span> at exactly the point when they’d normally give up”</h2>
          </div>
          <div className="cl-video-frame">
            <div className="cl-video-embed">
              <iframe
                src="https://player.vimeo.com/video/1211342568?h=4a42670a45&dnt=1"
                title="ClassLabs — reviewed by the experts"
                allow="autoplay; fullscreen; picture-in-picture"
                allowFullScreen
              />
            </div>
          </div>
        </div>
      </section>

      <StatsBanner />

      {/* What schools are saying */}
      <section className="cl-section cl-testi" id="testimonials">
        <div className="cl-wrap">
          <div className="cl-section-head cl-section-head--center">
            <div className="cl-eyebrow">From the classroom</div>
            <h2 className="cl-h2">“It’s <span className="cl-h2-accent">really helped</span> those children a lot”</h2>
          </div>
          <div className="cl-video-frame">
            <div className="cl-video-embed">
              <iframe
                src="https://player.vimeo.com/video/1211342134?h=b359b749d0&dnt=1"
                title="ClassLabs — from the classroom"
                allow="autoplay; fullscreen; picture-in-picture"
                allowFullScreen
              />
            </div>
          </div>
        </div>
      </section>

      {/* Backed and supported by */}
      <section className="cl-backed">
        <div className="cl-wrap">
          <p className="cl-backed-label">Backed and supported by</p>
          <div className="cl-backed-logos">
            <img src={uclLogo} alt="UCL" className="cl-backed-logo" style={{ '--h': 67 }} />
            <img src={lsvLogo} alt="LSV" className="cl-backed-logo" style={{ '--h': 41 }} />
            <img src={lldcLogo} alt="London Legacy Development Corporation" className="cl-backed-logo" style={{ '--h': 55 }} />
            <img src={googleLogo} alt="Google" className="cl-backed-logo" style={{ '--h': 130, '--mx': -22 }} />
            <img src={cdiLogo} alt="CDI" className="cl-backed-logo" style={{ '--h': 96 }} />
          </div>
        </div>
      </section>

      {/* Who it's for */}
      <section className="cl-section cl-roles">
        <div className="cl-wrap">
          <div className="cl-section-head cl-section-head--center">
            <div className="cl-eyebrow">Who it's for</div>
            <h2 className="cl-h2">Digital Teaching Assistant that <span className="cl-h2-accent">benefits every member of staff</span></h2>
          </div>
          <div className="cl-orbit">
            <div className="cl-orbit-vline" />
            <div className="cl-orbit-hline" />
            <div className="cl-orbit-hub">
              <span className="cl-orbit-hub-big">Students with SEND</span>
              <span className="cl-orbit-hub-sub">&amp; learning differences</span>
            </div>
            <div className="cl-orbit-card cl-orbit-n">
              <span className="cl-orbit-icon" style={{ background: 'var(--coral)' }}>🍎</span>
              <h3>Teachers</h3>
              <p>50% reduction in planning and content adaptation time.</p>
            </div>
            <div className="cl-orbit-card cl-orbit-e">
              <span className="cl-orbit-icon" style={{ background: 'var(--teal)' }}>🎯</span>
              <h3>SENCOs</h3>
              <p>Accurate information for support plan SMART targets and EHCP applications.</p>
            </div>
            <div className="cl-orbit-card cl-orbit-s">
              <span className="cl-orbit-icon" style={{ background: 'var(--coral)' }}>📊</span>
              <h3>Senior Leadership</h3>
              <p>Whole-school inclusion and oversight.</p>
            </div>
            <div className="cl-orbit-card cl-orbit-w">
              <span className="cl-orbit-icon" style={{ background: 'var(--teal)' }}>🤝</span>
              <h3>TAs &amp; Support Staff</h3>
              <p>A platform that works for interventions and 1-to-1, and more time to go the extra mile, e.g. designing sensory boards.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="cl-section cl-cta-final" id="cta">
        <div className="cl-wrap">
          <div className="cl-eyebrow" style={{ marginBottom: 24 }}>See it in action</div>
          <h2 className="cl-h2">Every tutorial as <span className="cl-cta-accent">unique as the student</span></h2>
          <p className="sub">Join the schools already using ClassLabs to adapt learning for every pupil. Leave your details, and we'll arrange a personalised demo.</p>
          <Link to="/register-interest" className="cl-btn-final">Book a demo</Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="cl-footer">
        <div className="cl-wrap">
          <div className="cl-footer-grid">
            <div className="cl-footer-brand">
              <img src={logo} alt="ClassLabs" className="cl-footer-logo" />
              <p>The Digital Teaching Assistant Trusted by UK SEND Units — adapting your content to every learner, backed by evidence and built for impact.</p>
              <div className="cl-footer-socials">
                <a href="mailto:info@classlabs.co.uk" className="cl-footer-social" aria-label="Email ClassLabs">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect width="20" height="16" x="2" y="4" rx="2" />
                    <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
                  </svg>
                </a>
                <a href="https://www.linkedin.com/company/classlabsuk/" target="_blank" rel="noopener noreferrer" className="cl-footer-social" aria-label="ClassLabs on LinkedIn">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                    <path d="M20.45 20.45h-3.55v-5.57c0-1.33-.02-3.04-1.85-3.04-1.85 0-2.14 1.45-2.14 2.94v5.67H9.36V9h3.41v1.56h.05c.48-.9 1.63-1.85 3.36-1.85 3.6 0 4.27 2.37 4.27 5.45v6.29zM5.34 7.43a2.06 2.06 0 1 1 0-4.12 2.06 2.06 0 0 1 0 4.12zM7.12 20.45H3.56V9h3.56v11.45zM22.22 0H1.77C.79 0 0 .77 0 1.73v20.54C0 23.23.79 24 1.77 24h20.45c.98 0 1.78-.77 1.78-1.73V1.73C24 .77 23.2 0 22.22 0z" />
                  </svg>
                </a>
              </div>
            </div>
            <div className="cl-footer-col">
              <h4>Quick Links</h4>
              <ul>
                <li><a href="#offers">What we offer</a></li>
                <li><a href="#testimonials">What schools say</a></li>
                <li><Link to="/register-interest">Book a demo</Link></li>
                <li><Link to="/login">Log in</Link></li>
              </ul>
            </div>
            <div className="cl-footer-col">
              <h4>Legal</h4>
              <ul>
                <li><Link to="/privacy-policy">Privacy Policy</Link></li>
                <li><a href="mailto:info@classlabs.co.uk">Contact Us</a></li>
                <li><button type="button" className="cl-footer-cookie-btn" onClick={openCookieSettings}>Cookie settings</button></li>
              </ul>
            </div>
          </div>
          <div className="cl-footer-legal">
            <p>© 2026 ClassCompass Ltd. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
